/**
 * Portfolio — demat holdings and open positions, proxied live from the broker
 * via /brokers/holdings and /brokers/positions, with Buy/Sell on holdings,
 * Add/Exit on positions and a one-click square-off of every open position.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase, CheckSquare, Layers, LogOut, Plug, Plus, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { useMarketStore } from '@/stores/market-store';
import { useMarketWebSocket } from '@/hooks/use-websocket';
import { cn } from '@/lib/cn';
import { formatINR, formatNumber, formatPct, formatQty, signClass, signOf, toNum } from '@/lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  SkeletonRows,
  StatCard,
  Switch,
  Table,
  TableMessage,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Tabs,
} from '@/components/ui';
import { OrderTicket } from '@/features/orders/components/OrderTicket';
import type { OrderTicketPrefill } from '@/features/orders/components/OrderTicket';

interface HoldingData {
  trading_symbol: string;
  instrument_token: string;
  exchange_segment: string;
  isin: string;
  quantity: number;
  average_price: string;
  current_price: string;
  pnl: string;
  pnl_percentage: string;
  day_change: string;
  day_change_percentage: string;
  previous_close?: string;
}

interface PositionData {
  exchange_segment: string;
  trading_symbol: string;
  instrument_token: string;
  product: string;
  buy_quantity: number;
  buy_average_price: string;
  buy_value: string;
  sell_quantity: number;
  sell_average_price: string;
  sell_value: string;
  net_quantity: number;
  realized_pnl: string;
  unrealized_pnl: string;
  current_price: string;
  lot_size?: number;
  previous_close?: string;
  /** Entry average of the net position (from the holdings book for carried legs). */
  average_price?: string;
  /** Today's mark-to-market including anything realized today. */
  day_pnl?: string;
  carry_forward_quantity?: number;
}

interface ConnectionInfo {
  connection_id: string;
  broker_type: string;
  display_name: string;
  ucc: string;
  is_active: boolean;
  is_session_valid: boolean;
  session_expires_at: string | null;
  last_connected_at: string | null;
}

interface SquareOffLeg {
  trading_symbol: string;
  side: string;
  quantity: number;
  status: string;
  broker_order_id: string;
  message: string;
  violations: string[];
}

interface SquareOffResult {
  batch_id: string;
  requested: number;
  placed: number;
  rejected: number;
  legs: SquareOffLeg[];
}

// ── Sample data — rendered only with the "Sample data" badge visible ────────

const DEMO_HOLDINGS: HoldingData[] = [
  { trading_symbol: 'RELIANCE', instrument_token: '2885', exchange_segment: 'nse_cm', isin: 'INE002A01018', quantity: 150, average_price: '2450.50', current_price: '2548.75', pnl: '14737.50', pnl_percentage: '4.01', day_change: '32.10', day_change_percentage: '1.27' },
  { trading_symbol: 'TCS', instrument_token: '11536', exchange_segment: 'nse_cm', isin: 'INE467B01029', quantity: 80, average_price: '3210.00', current_price: '3452.40', pnl: '19392.00', pnl_percentage: '7.55', day_change: '-12.80', day_change_percentage: '-0.37' },
  { trading_symbol: 'HDFCBANK', instrument_token: '1333', exchange_segment: 'nse_cm', isin: 'INE040A01034', quantity: 250, average_price: '1520.40', current_price: '1615.10', pnl: '23675.00', pnl_percentage: '6.23', day_change: '18.45', day_change_percentage: '1.16' },
  { trading_symbol: 'INFY', instrument_token: '1594', exchange_segment: 'nse_cm', isin: 'INE009A01021', quantity: 120, average_price: '1420.00', current_price: '1398.50', pnl: '-2580.00', pnl_percentage: '-1.82', day_change: '4.20', day_change_percentage: '0.30' },
];

const DEMO_POSITIONS: PositionData[] = [
  { exchange_segment: 'nse_fo', trading_symbol: 'NIFTY26JUN22000CE', instrument_token: '55421', product: 'MIS', buy_quantity: 150, buy_average_price: '125.40', buy_value: '18810.00', sell_quantity: 0, sell_average_price: '0.00', sell_value: '0.00', net_quantity: 150, realized_pnl: '0.00', unrealized_pnl: '3450.00', current_price: '148.40', lot_size: 75 },
  { exchange_segment: 'nse_cm', trading_symbol: 'TATAMOTORS', instrument_token: '3456', product: 'MIS', buy_quantity: 500, buy_average_price: '640.20', buy_value: '320100.00', sell_quantity: 500, sell_average_price: '645.80', sell_value: '322900.00', net_quantity: 0, realized_pnl: '2800.00', unrealized_pnl: '0.00', current_price: '646.10' },
];

const TABS = [
  { id: 'holdings' as const, label: 'Holdings', icon: Briefcase },
  { id: 'positions' as const, label: 'Positions', icon: Layers },
];

const PORTFOLIO_POLL_MS = 10_000;

function isQuotable(token: string | undefined): token is string {
  return Boolean(token) && /^\d+$/.test(token as string) && token !== '0';
}

/** Kotak lists F&O carry-forward legs in the holdings book too; they belong on the Positions tab. */
function isDerivativeHolding(h: HoldingData): boolean {
  return (h.exchange_segment || '').toLowerCase().endsWith('_fo');
}

function entryAverage(p: PositionData): number {
  const avg = toNum(p.average_price);
  if (avg > 0) return avg;
  return p.net_quantity >= 0 ? toNum(p.buy_average_price) : toNum(p.sell_average_price);
}

/** Overall open P&L for a live price; sign-aware for shorts. */
function openPnl(p: PositionData, ltp: number): number {
  const avg = entryAverage(p);
  if (p.net_quantity === 0 || avg <= 0) return toNum(p.unrealized_pnl);
  return (ltp - avg) * p.net_quantity;
}

type Product = 'CNC' | 'MIS' | 'NRML';
type SquareMode = 'all' | 'selected';

/** A position is unique per instrument + product at the broker. */
function positionKey(p: PositionData): string {
  return `${p.instrument_token}-${p.product}`;
}

function asProduct(value: string): Product {
  const upper = value.toUpperCase();
  return upper === 'CNC' || upper === 'NRML' ? upper : 'MIS';
}

export default function PortfolioPage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'holdings' | 'positions'>('holdings');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connections, setConnections] = useState<ConnectionInfo[]>([]);
  const [holdings, setHoldings] = useState<HoldingData[]>([]);
  const [positions, setPositions] = useState<PositionData[]>([]);
  const [showDemo, setShowDemo] = useState(false);

  // Trading actions
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketPrefill, setTicketPrefill] = useState<OrderTicketPrefill | null>(null);
  const [exitTarget, setExitTarget] = useState<PositionData | null>(null);
  const [exiting, setExiting] = useState(false);
  const [squareAllOpen, setSquareAllOpen] = useState(false);
  const [squareMode, setSquareMode] = useState<SquareMode>('all');
  const [squareMisOnly, setSquareMisOnly] = useState(false);
  const [squaringAll, setSquaringAll] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [notice, setNotice] = useState<{ ok: boolean; title: string; lines: string[] } | null>(null);

  const activeConnection = connections.find((c) => c.is_active && c.is_session_valid);
  const ticks = useMarketStore((s) => s.ticks);
  const { isConnected, feedAttached, subscribe } = useMarketWebSocket();

  const fetchPortfolioData = async (connectionId?: string) => {
    try {
      setError(null);
      const connParam = connectionId ? `?connection_id=${connectionId}` : '';
      const [hRes, pRes] = await Promise.all([
        api.get(`/brokers/holdings${connParam}`),
        api.get(`/brokers/positions${connParam}`),
      ]);
      setHoldings(hRes.data);
      setPositions(pRes.data);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const initData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/brokers/status');
      setConnections(data);
      const active = data.find((c: ConnectionInfo) => c.is_active && c.is_session_valid);
      if (active) await fetchPortfolioData(active.connection_id);
    } catch {
      setError('Failed to fetch broker status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!activeConnection && !showDemo) return;
    setRefreshing(true);
    try {
      if (showDemo) {
        await new Promise((resolve) => setTimeout(resolve, 800));
      } else if (activeConnection) {
        await fetchPortfolioData(activeConnection.connection_id);
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Positions change with every fill — keep the books fresh while a broker is connected.
  const activeConnectionId = activeConnection?.connection_id;
  useEffect(() => {
    if (!activeConnectionId || showDemo) return;
    const timer = setInterval(() => fetchPortfolioData(activeConnectionId), PORTFOLIO_POLL_MS);
    return () => clearInterval(timer);
  }, [activeConnectionId, showDemo]);

  // Stream the instruments on screen so LTP and P&L move between polls.
  const liveTokens = useMemo(() => {
    const tokens = new Set<string>();
    for (const h of holdings) if (isQuotable(h.instrument_token)) tokens.add(h.instrument_token);
    for (const p of positions) if (isQuotable(p.instrument_token)) tokens.add(p.instrument_token);
    return [...tokens];
  }, [holdings, positions]);

  useEffect(() => {
    if (isConnected && !showDemo && liveTokens.length > 0) subscribe(liveTokens);
  }, [isConnected, showDemo, liveTokens, subscribe]);

  const liveLtp = (token: string | undefined): number | null => {
    if (showDemo || !isQuotable(token)) return null;
    const t = ticks[token];
    const ltp = t ? toNum(t.ltp) : 0;
    return ltp > 0 ? ltp : null;
  };

  const derivativeHoldingCount = useMemo(() => (showDemo ? 0 : holdings.filter(isDerivativeHolding).length), [holdings, showDemo]);

  const visibleHoldings = useMemo(() => {
    const base = showDemo ? DEMO_HOLDINGS : holdings.filter((h) => !isDerivativeHolding(h));
    return base.map((h) => {
      const ltp = liveLtp(h.instrument_token);
      if (ltp === null) return h;
      const prev = toNum(h.previous_close) || toNum(h.current_price);
      const avg = toNum(h.average_price);
      const pnl = avg > 0 ? (ltp - avg) * h.quantity : toNum(h.pnl);
      const invested = Math.abs(avg * h.quantity);
      return {
        ...h,
        current_price: String(ltp),
        day_change: prev > 0 ? String(ltp - prev) : h.day_change,
        day_change_percentage: prev > 0 ? String(((ltp - prev) / prev) * 100) : h.day_change_percentage,
        pnl: String(pnl),
        pnl_percentage: invested > 0 ? String((pnl / invested) * 100) : h.pnl_percentage,
      };
    });
    // liveLtp reads `ticks`, which is in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings, showDemo, ticks]);

  const visiblePositions = useMemo(() => {
    const base = showDemo ? DEMO_POSITIONS : positions;
    return base.map((p) => {
      const ltp = liveLtp(p.instrument_token);
      if (ltp === null) return p;
      // A price move shifts today's and overall P&L by the same amount.
      const delta = (ltp - toNum(p.current_price)) * p.net_quantity;
      return {
        ...p,
        current_price: String(ltp),
        unrealized_pnl: String(openPnl(p, ltp)),
        day_pnl: String(toNum(p.day_pnl) + delta),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, showDemo, ticks]);

  const totals = useMemo(() => {
    const invested = visibleHoldings.reduce((sum, h) => sum + toNum(h.average_price) * h.quantity, 0);
    const current = visibleHoldings.reduce((sum, h) => sum + toNum(h.current_price) * h.quantity, 0);
    const holdingsPnl = visibleHoldings.reduce((sum, h) => sum + toNum(h.pnl), 0);
    const positionsPnl = visiblePositions.reduce((sum, p) => sum + toNum(p.unrealized_pnl), 0);
    const positionsDayPnl = visiblePositions.reduce(
      (sum, p) => sum + (p.day_pnl !== undefined ? toNum(p.day_pnl) : toNum(p.unrealized_pnl) + toNum(p.realized_pnl)),
      0
    );
    return {
      invested,
      current,
      holdingsPnl,
      holdingsPnlPct: invested > 0 ? (holdingsPnl / invested) * 100 : 0,
      positionsPnl,
      positionsDayPnl,
      openPositions: visiblePositions.filter((p) => p.net_quantity !== 0).length,
    };
  }, [visibleHoldings, visiblePositions]);

  const connected = Boolean(activeConnection);
  const showingData = connected || showDemo;
  const canTrade = connected && !showDemo;
  const openPositions = useMemo(() => visiblePositions.filter((p) => p.net_quantity !== 0), [visiblePositions]);
  // Selection is matched against the live rows, so keys for positions that have since closed simply drop out.
  const selectedPositions = useMemo(
    () => openPositions.filter((p) => selectedKeys.has(positionKey(p))),
    [openPositions, selectedKeys]
  );
  const allOpenSelected = openPositions.length > 0 && selectedPositions.length === openPositions.length;
  const squareTargets =
    squareMode === 'selected'
      ? selectedPositions
      : squareMisOnly
        ? openPositions.filter((p) => p.product.toUpperCase() === 'MIS')
        : openPositions;

  const toggleSelected = (p: PositionData) => {
    const key = positionKey(p);
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllOpen = () => {
    setSelectedKeys(allOpenSelected ? new Set() : new Set(openPositions.map(positionKey)));
  };

  const openSquareOff = (mode: SquareMode) => {
    setSquareMode(mode);
    setSquareAllOpen(true);
  };

  // ── Actions ───────────────────────────────────────────────────────────

  const openTicket = (prefill: OrderTicketPrefill) => {
    setTicketPrefill(prefill);
    setTicketOpen(true);
  };

  const tradeHolding = (h: HoldingData, side: 'B' | 'S') =>
    openTicket({
      exchange_segment: h.exchange_segment || 'nse_cm',
      trading_symbol: h.trading_symbol,
      instrument_token: h.instrument_token,
      transaction_type: side,
      product: 'CNC',
      ltp: h.current_price,
      quantity: side === 'S' ? h.quantity : undefined,
    });

  const addToPosition = (p: PositionData) =>
    openTicket({
      exchange_segment: p.exchange_segment,
      trading_symbol: p.trading_symbol,
      instrument_token: p.instrument_token,
      transaction_type: p.net_quantity >= 0 ? 'B' : 'S',
      product: asProduct(p.product),
      lot_size: p.lot_size && p.lot_size > 1 ? p.lot_size : undefined,
      ltp: p.current_price,
      quantity: p.lot_size && p.lot_size > 1 ? p.lot_size : Math.abs(p.net_quantity) || undefined,
    });

  const exitWithTicket = (p: PositionData) => {
    setExitTarget(null);
    openTicket({
      exchange_segment: p.exchange_segment,
      trading_symbol: p.trading_symbol,
      instrument_token: p.instrument_token,
      transaction_type: p.net_quantity > 0 ? 'S' : 'B',
      product: asProduct(p.product),
      lot_size: p.lot_size && p.lot_size > 1 ? p.lot_size : undefined,
      ltp: p.current_price,
      quantity: Math.abs(p.net_quantity),
    });
  };

  const describeResult = (title: string, result: SquareOffResult) => {
    setNotice({
      ok: result.rejected === 0,
      title: `${title}: ${result.placed} of ${result.requested} exit order${result.requested === 1 ? '' : 's'} sent`,
      lines: result.legs.map(
        (leg) =>
          `${leg.side === 'B' ? 'BUY' : 'SELL'} ${formatQty(leg.quantity)} ${leg.trading_symbol} · ${leg.status}` +
          (leg.broker_order_id ? ` · #${leg.broker_order_id}` : '') +
          (leg.status !== 'sent' && (leg.violations?.length ? ` · ${leg.violations.join(', ')}` : leg.message ? ` · ${leg.message}` : ''))
      ),
    });
  };

  const confirmExit = async () => {
    if (!exitTarget || !activeConnection) return;
    setExiting(true);
    try {
      const { data } = await api.post<SquareOffResult>('/orders/square-off', {
        connection_id: activeConnection.connection_id,
        positions: [
          {
            instrument_token: exitTarget.instrument_token,
            exchange_segment: exitTarget.exchange_segment,
            trading_symbol: exitTarget.trading_symbol,
            product: exitTarget.product,
            net_quantity: exitTarget.net_quantity,
          },
        ],
      });
      describeResult(`Exit ${exitTarget.trading_symbol}`, data);
      setExitTarget(null);
      await fetchPortfolioData(activeConnection.connection_id);
    } catch (err) {
      setNotice({ ok: false, title: 'Exit failed', lines: [getErrorMessage(err)] });
    } finally {
      setExiting(false);
    }
  };

  const confirmSquareAll = async () => {
    if (!activeConnection || squareTargets.length === 0) return;
    setSquaringAll(true);
    try {
      const body =
        squareMode === 'selected'
          ? {
              connection_id: activeConnection.connection_id,
              positions: squareTargets.map((p) => ({
                instrument_token: p.instrument_token,
                exchange_segment: p.exchange_segment,
                trading_symbol: p.trading_symbol,
                product: p.product,
                net_quantity: p.net_quantity,
              })),
            }
          : {
              connection_id: activeConnection.connection_id,
              all: true,
              product: squareMisOnly ? 'MIS' : null,
            };
      const { data } = await api.post<SquareOffResult>('/orders/square-off', body);
      describeResult(
        squareMode === 'selected' ? 'Square off selected' : squareMisOnly ? 'Square off MIS' : 'Square off all',
        data
      );
      setSquareAllOpen(false);
      setSelectedKeys(new Set());
      await fetchPortfolioData(activeConnection.connection_id);
    } catch (err) {
      setNotice({ ok: false, title: 'Square off failed', lines: [getErrorMessage(err)] });
    } finally {
      setSquaringAll(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Briefcase}
        title="Portfolio"
        description="Demat holdings and open positions — trade, add or exit from here"
        meta={
          <>
            {showDemo ? (
              <Badge tone="warning">Sample data</Badge>
            ) : connected ? (
              <Badge tone="success">{activeConnection?.display_name ?? 'Connected'}</Badge>
            ) : (
              <Badge tone="neutral">Not connected</Badge>
            )}
            {connected && !showDemo && (
              <Badge
                tone={isConnected && feedAttached ? 'success' : 'warning'}
                icon={isConnected && feedAttached ? Wifi : WifiOff}
              >
                {isConnected && feedAttached ? 'Live prices' : 'Prices refresh every 10s'}
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <Button
              variant={showDemo ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowDemo((v) => !v)}
              aria-pressed={showDemo}
            >
              {showDemo ? 'Showing sample data' : 'Show sample data'}
            </Button>
            {canTrade && selectedPositions.length > 0 && (
              <Button variant="danger" size="sm" icon={CheckSquare} onClick={() => openSquareOff('selected')}>
                Square off selected ({selectedPositions.length})
              </Button>
            )}
            {canTrade && openPositions.length > 0 && (
              <Button variant="secondary" size="sm" icon={LogOut} onClick={() => openSquareOff('all')}>
                Square off all
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              loading={refreshing}
              disabled={!showingData}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </>
        }
      />

      {notice && (
        <Alert tone={notice.ok ? 'success' : 'warning'} title={notice.title} onDismiss={() => setNotice(null)}>
          <ul className="mt-1 flex flex-col gap-0.5 font-tabular text-xs">
            {notice.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="mt-1 text-xs opacity-80">Track fills on the Orders page.</p>
        </Alert>
      )}

      {error && (
        <Alert
          tone="danger"
          title="Couldn't load portfolio"
          action={
            <Button variant="secondary" size="sm" onClick={initData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!connected && !loading && !showDemo && (
        <Alert
          tone="warning"
          title="No broker connected"
          action={
            <Button variant="secondary" size="sm" iconRight={ArrowRight} onClick={() => navigate('/settings')}>
              Connect
            </Button>
          }
        >
          Connect Kotak Neo to load your holdings and positions, or switch on sample data to explore the screen.
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Invested" value={formatINR(totals.invested)} loading={loading} />
        <StatCard label="Current value" value={formatINR(totals.current)} loading={loading} />
        <StatCard
          label="Holdings P&L"
          value={<span className={signClass(totals.holdingsPnl)}>{formatINR(totals.holdingsPnl)}</span>}
          delta={{ value: formatPct(totals.holdingsPnlPct), direction: signOf(totals.holdingsPnl) }}
          loading={loading}
        />
        <StatCard
          label="Positions P&L (overall)"
          value={<span className={signClass(totals.positionsPnl)}>{formatINR(totals.positionsPnl)}</span>}
          hint={
            <span>
              Today <span className={cn('font-tabular', signClass(totals.positionsDayPnl))}>{formatINR(totals.positionsDayPnl)}</span> · {totals.openPositions} open
            </span>
          }
          loading={loading}
        />
      </div>

      <Card>
        <div className="border-b border-line px-4 py-3">
          <Tabs
            aria-label="Portfolio view"
            items={TABS.map((t) => ({
              ...t,
              count: t.id === 'holdings' ? visibleHoldings.length : visiblePositions.length,
            }))}
            value={activeTab}
            onChange={setActiveTab}
          />
          {activeTab === 'holdings' && derivativeHoldingCount > 0 && (
            <p className="mt-2 text-xs text-fg-subtle">
              {derivativeHoldingCount} F&O carry-forward leg{derivativeHoldingCount === 1 ? '' : 's'} from the broker's holdings book are shown under Positions.
            </p>
          )}
        </div>

        {activeTab === 'holdings' ? (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>Instrument</TH>
                  <TH align="right">Qty</TH>
                  <TH align="right">Avg cost</TH>
                  <TH align="right">LTP</TH>
                  <TH align="right">Day change</TH>
                  <TH align="right">P&L</TH>
                  <TH align="right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {loading ? (
                  <SkeletonRows rows={4} cols={7} />
                ) : visibleHoldings.length === 0 ? (
                  <TableMessage colSpan={7}>
                    <EmptyState
                      icon={Plug}
                      title="No holdings"
                      description={
                        connected
                          ? 'Your demat holdings will appear here.'
                          : 'Connect a broker to load your demat holdings.'
                      }
                    />
                  </TableMessage>
                ) : (
                  visibleHoldings.map((h) => (
                    <TR key={`${h.instrument_token}-${h.isin}`}>
                      <TD>
                        <div className="font-medium text-fg">{h.trading_symbol}</div>
                        <div className="font-tabular text-xs text-fg-faint uppercase">
                          {h.exchange_segment?.replace('_', ' ')}
                        </div>
                      </TD>
                      <TD align="right" className="font-tabular text-fg-muted">
                        {formatQty(h.quantity)}
                      </TD>
                      <TD align="right" className="font-tabular text-fg-muted">
                        {formatNumber(h.average_price)}
                      </TD>
                      <TD align="right" className="font-tabular text-fg">
                        {formatNumber(h.current_price)}
                      </TD>
                      <TD align="right" className={cn('font-tabular', signClass(h.day_change))}>
                        {formatNumber(h.day_change)} ({formatPct(h.day_change_percentage)})
                      </TD>
                      <TD align="right" className={cn('font-tabular font-medium', signClass(h.pnl))}>
                        <div>{formatINR(h.pnl)}</div>
                        <div className="text-xs">{formatPct(h.pnl_percentage)}</div>
                      </TD>
                      <TD align="right">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="text-buy"
                            disabled={!canTrade}
                            title={canTrade ? undefined : 'Connect a broker (live data) to trade'}
                            onClick={() => tradeHolding(h, 'B')}
                          >
                            Buy
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={!canTrade || h.quantity <= 0}
                            title={canTrade ? undefined : 'Connect a broker (live data) to trade'}
                            onClick={() => tradeHolding(h, 'S')}
                          >
                            Sell
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </TableWrap>
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH className="w-8">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer rounded border-line-strong bg-surface-2 accent-brand disabled:cursor-not-allowed disabled:opacity-40"
                      checked={allOpenSelected}
                      disabled={!canTrade || openPositions.length === 0}
                      onChange={toggleAllOpen}
                      aria-label={allOpenSelected ? 'Deselect all open positions' : 'Select all open positions'}
                    />
                  </TH>
                  <TH>Instrument</TH>
                  <TH>Product</TH>
                  <TH align="right">Net qty</TH>
                  <TH align="right">Avg</TH>
                  <TH align="right">LTP</TH>
                  <TH align="right">P&L</TH>
                  <TH align="right">Today</TH>
                  <TH align="right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {loading ? (
                  <SkeletonRows rows={3} cols={10} />
                ) : visiblePositions.length === 0 ? (
                  <TableMessage colSpan={10}>
                    <EmptyState
                      icon={Layers}
                      title="No open positions"
                      description={
                        connected
                          ? 'Intraday and carry-forward positions appear here.'
                          : 'Connect a broker to load your positions.'
                      }
                    />
                  </TableMessage>
                ) : (
                  visiblePositions.map((p) => {
                    const pnl = toNum(p.unrealized_pnl);
                    const dayPnl = p.day_pnl !== undefined ? toNum(p.day_pnl) : pnl + toNum(p.realized_pnl);
                    const avg = entryAverage(p);
                    const carried = (p.carry_forward_quantity ?? 0) !== 0;
                    const closed = p.net_quantity === 0;
                    const long = p.net_quantity > 0;
                    const lots = p.lot_size && p.lot_size > 1 ? Math.abs(p.net_quantity) / p.lot_size : null;
                    const selected = selectedKeys.has(positionKey(p));
                    return (
                      <TR key={positionKey(p)} className={cn(selected && 'bg-brand/8')}>
                        <TD>
                          {!closed && (
                            <input
                              type="checkbox"
                              className="h-4 w-4 cursor-pointer rounded border-line-strong bg-surface-2 accent-brand disabled:cursor-not-allowed disabled:opacity-40"
                              checked={selected}
                              disabled={!canTrade}
                              onChange={() => toggleSelected(p)}
                              aria-label={`Select ${p.trading_symbol} for square off`}
                            />
                          )}
                        </TD>
                        <TD>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-fg">{p.trading_symbol}</span>
                            {closed ? (
                              <Badge tone="neutral">Closed</Badge>
                            ) : (
                              <Badge tone={long ? 'info' : 'danger'}>{long ? 'Long' : 'Short'}</Badge>
                            )}
                          </div>
                          <div className="font-tabular text-xs text-fg-faint uppercase">
                            {p.exchange_segment?.replace('_', ' ')}
                          </div>
                        </TD>
                        <TD className="text-xs text-fg-muted">{p.product}</TD>
                        <TD align="right" className="font-tabular text-fg-muted">
                          <div className={cn(!closed && (long ? 'text-buy' : 'text-sell'))}>{formatQty(p.net_quantity)}</div>
                          {lots !== null && <div className="text-xs text-fg-faint">{formatNumber(lots)} lot{lots === 1 ? '' : 's'}</div>}
                        </TD>
                        <TD align="right" className="font-tabular text-fg-muted">
                          <div>{avg > 0 ? formatNumber(avg) : '—'}</div>
                          {carried && toNum(p.previous_close) > 0 && (
                            <div className="text-xs text-fg-faint">prev close {formatNumber(p.previous_close)}</div>
                          )}
                        </TD>
                        <TD align="right" className="font-tabular text-fg">
                          {formatNumber(p.current_price)}
                        </TD>
                        <TD align="right" className={cn('font-tabular font-medium', signClass(pnl))}>
                          <div>{closed ? formatINR(p.realized_pnl) : formatINR(pnl)}</div>
                          <div className="text-xs text-fg-faint">{closed ? 'realized' : 'vs entry avg'}</div>
                        </TD>
                        <TD align="right" className={cn('font-tabular', signClass(dayPnl))}>
                          <div>{formatINR(dayPnl)}</div>
                          {toNum(p.realized_pnl) !== 0 && !closed && (
                            <div className="text-xs text-fg-faint">incl. {formatINR(p.realized_pnl)} realized</div>
                          )}
                        </TD>
                        <TD align="right">
                          {closed ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Plus}
                              disabled={!canTrade}
                              onClick={() =>
                                openTicket({
                                  exchange_segment: p.exchange_segment,
                                  trading_symbol: p.trading_symbol,
                                  instrument_token: p.instrument_token,
                                  transaction_type: 'B',
                                  product: asProduct(p.product),
                                  lot_size: p.lot_size && p.lot_size > 1 ? p.lot_size : undefined,
                                  ltp: p.current_price,
                                })
                              }
                            >
                              Re-enter
                            </Button>
                          ) : (
                            <div className="flex justify-end gap-1.5">
                              <Button
                                variant="secondary"
                                size="sm"
                                icon={Plus}
                                disabled={!canTrade}
                                title={canTrade ? `${long ? 'Buy' : 'Sell'} more ${p.trading_symbol}` : 'Connect a broker (live data) to trade'}
                                onClick={() => addToPosition(p)}
                              >
                                Add
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                icon={LogOut}
                                disabled={!canTrade}
                                title={canTrade ? `Square off ${p.trading_symbol}` : 'Connect a broker (live data) to trade'}
                                onClick={() => setExitTarget(p)}
                              >
                                Exit
                              </Button>
                            </div>
                          )}
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <OrderTicket
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        prefill={ticketPrefill}
        onPlaced={() => {
          if (activeConnection) fetchPortfolioData(activeConnection.connection_id);
        }}
      />

      {/* Exit one position */}
      <Modal
        open={exitTarget !== null}
        onClose={() => setExitTarget(null)}
        title={exitTarget ? `Exit ${exitTarget.trading_symbol}?` : 'Exit position'}
        description="Places a market order on the opposite side for the full net quantity, through the risk engine."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setExitTarget(null)}>
              Keep position
            </Button>
            <Button variant="secondary" onClick={() => exitTarget && exitWithTicket(exitTarget)}>
              Exit with limit…
            </Button>
            <Button variant="danger" icon={LogOut} loading={exiting} onClick={confirmExit}>
              Exit at market
            </Button>
          </>
        }
      >
        {exitTarget && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-fg-subtle">Order</dt>
            <dd className={cn('font-tabular font-medium', exitTarget.net_quantity > 0 ? 'text-sell' : 'text-buy')}>
              {exitTarget.net_quantity > 0 ? 'SELL' : 'BUY'} {formatQty(Math.abs(exitTarget.net_quantity))}
            </dd>
            <dt className="text-fg-subtle">Product</dt>
            <dd className="font-tabular text-fg">{exitTarget.product}</dd>
            <dt className="text-fg-subtle">Entry avg → LTP</dt>
            <dd className="font-tabular text-fg">
              {formatNumber(entryAverage(exitTarget))} → {formatNumber(exitTarget.current_price)}
            </dd>
            <dt className="text-fg-subtle">Open P&L</dt>
            <dd className={cn('font-tabular', signClass(exitTarget.unrealized_pnl))}>{formatINR(exitTarget.unrealized_pnl)}</dd>
          </dl>
        )}
      </Modal>

      {/* Square off all / selected */}
      <Modal
        open={squareAllOpen}
        onClose={() => setSquareAllOpen(false)}
        title={squareMode === 'selected' ? `Square off ${squareTargets.length} selected position${squareTargets.length === 1 ? '' : 's'}?` : 'Square off open positions?'}
        description="One market exit per position, buys and sells as needed. Each goes through the risk engine."
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSquareAllOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" icon={LogOut} loading={squaringAll} disabled={squareTargets.length === 0} onClick={confirmSquareAll}>
              Exit {squareTargets.length} position{squareTargets.length === 1 ? '' : 's'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {squareMode === 'all' && (
            <Switch checked={squareMisOnly} onChange={setSquareMisOnly} label="Only intraday (MIS) positions" />
          )}
          <ul className="max-h-64 divide-y divide-line overflow-y-auto rounded-lg border border-line">
            {squareTargets.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-fg-subtle">
                {squareMode === 'selected' ? 'No open positions are selected.' : 'Nothing to square off with this filter.'}
              </li>
            ) : (
              squareTargets.map((p) => (
                <li key={`${p.instrument_token}-${p.product}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2">
                    <span className={cn('text-xs font-semibold', p.net_quantity > 0 ? 'text-sell' : 'text-buy')}>
                      {p.net_quantity > 0 ? 'SELL' : 'BUY'}
                    </span>
                    <span className="font-tabular text-fg">{p.trading_symbol}</span>
                    <Badge tone="neutral">{p.product}</Badge>
                  </span>
                  <span className="font-tabular text-xs text-fg-muted">{formatQty(Math.abs(p.net_quantity))} @ MKT</span>
                </li>
              ))
            )}
          </ul>
        </div>
      </Modal>
    </div>
  );
}
